import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getApiErrorMessage } from '../api';
import AppShell from './AppShell';
import AdvancedPurchase from './AdvancedPurchase';
import ReferralCard from './ReferralCard';
import NewsletterCard from './NewsletterCard';
import './Profile.css';

function Profile() {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdMessage, setPwdMessage] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [isChangingPwd, setIsChangingPwd] = useState(false);

  const [unlinkMessage, setUnlinkMessage] = useState('');
  const [unlinkError, setUnlinkError] = useState('');
  const [isUnlinking, setIsUnlinking] = useState(false);

  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [logoMessage, setLogoMessage] = useState('');

  const [telegramWebAuthEnabled, setTelegramWebAuthEnabled] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
    api.get('/auth/config')
      .then((res) => setTelegramWebAuthEnabled(res.data.telegram_web_auth_enabled !== false))
      .catch(() => setTelegramWebAuthEnabled(false));
    
    // Check URL for payment return
    if (window.location.search.includes('payment=pending')) {
      setLogoMessage('Проверка статуса платежа. Обычно это занимает несколько минут. Пожалуйста, обновите страницу позже.');
      // remove query param
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Переход «Тариф и оплата» из меню: /profile#tariffs — прокручиваем, когда профиль загрузится
  useEffect(() => {
    if (isLoading || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isLoading]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/users/me/profile');
      setProfile(response.data);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate('/login');
      }
      console.error('Error fetching profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwdMessage('');
    setPwdError('');

    if (newPassword !== confirmPassword) {
      setPwdError('Новые пароли не совпадают');
      return;
    }

    if (newPassword.length < 6) {
      setPwdError('Новый пароль должен содержать минимум 6 символов');
      return;
    }

    setIsChangingPwd(true);
    try {
      await api.post('/users/me/change-password', {
        old_password: oldPassword,
        new_password: newPassword
      });
      setPwdMessage('Пароль успешно изменен');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwdError(getApiErrorMessage(err, 'Ошибка при изменении пароля'));
    } finally {
      setIsChangingPwd(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    if (!window.confirm('Вы уверены, что хотите отвязать Telegram аккаунт? Вы больше не сможете входить через бота, пока не привяжете его снова.')) {
      return;
    }

    setUnlinkMessage('');
    setUnlinkError('');
    setIsUnlinking(true);

    try {
      await api.post('/users/me/unlink-telegram');
      setUnlinkMessage('Telegram аккаунт успешно отвязан');
      fetchProfile();
    } catch (err) {
      setUnlinkError(getApiErrorMessage(err, 'Ошибка при отвязке Telegram'));
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setLogoError('Пожалуйста, выберите изображение');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('Размер файла не должен превышать 2МБ');
      return;
    }

    setLogoError('');
    setLogoMessage('');
    setIsUploadingLogo(true);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/users/me/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile({ ...profile, company_logo_path: response.data.logo_path });
      setLogoMessage('Логотип успешно загружен!');
    } catch (err) {
      setLogoError(getApiErrorMessage(err, 'Ошибка при загрузке логотипа'));
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Уверены, что хотите удалить логотип?')) return;
    
    setIsUploadingLogo(true);
    setLogoError('');
    setLogoMessage('');
    try {
      await api.delete('/users/me/logo');
      setProfile({ ...profile, company_logo_path: null });
      setLogoMessage('Логотип удален');
    } catch (err) {
      setLogoError('Ошибка при удалении логотипа');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  if (isLoading) return <div className="profile-loading">Загрузка профиля...</div>;
  if (!profile) return <div className="profile-page profile-loading">Ошибка загрузки профиля</div>;

  const isTelegramOnly = !profile.email;
  const canUnlink = profile.email && profile.telegram_id;

  const tierClass = profile.subscription_tier === 'corporate'
    ? 'tier-pill tier-pill--corporate'
    : profile.subscription_tier === 'advanced'
      ? 'tier-pill tier-pill--advanced'
      : 'tier-pill tier-pill--free';

  // null — без ограничений (корпоративный тариф)
  const pollMax = profile.subscription_tier === 'corporate' ? null : profile.subscription_tier === 'advanced' ? 15 : 2;
  const activeMax = profile.subscription_tier === 'corporate' ? 25 : profile.subscription_tier === 'advanced' ? 5 : 1;
  const meterColor = profile.subscription_tier === 'corporate' ? 'var(--color-info)' : 'var(--color-success)';

  return (
    <AppShell
      user={profile}
      counts={{ total: profile.poll_count || 0, active: profile.active_poll_count || 0, archived: profile.archived_poll_count || 0 }}
      section="profile"
      title="Профиль и тариф"
    >
    <div className="profile-page">
      <main className="profile-main">
        <h1 className="profile-page-title">Профиль и тариф</h1>
        
        {isTelegramOnly && (
          <div className="profile-warning">
            {telegramWebAuthEnabled ? (
              <>
                <h3>Рекомендуется привязать Email</h3>
                <p style={{ marginBottom: 0 }}>
                  В связи с возможной нестабильной работой Telegram, настоятельно рекомендуем зарегистрировать Email и установить пароль для безопасного доступа к вашему аккаунту.
                  <br /><br />
                  Вы можете сделать это, выйдя из аккаунта и пройдя регистрацию по тому же адресу, если вы его указывали, или обратившись в поддержку.
                </p>
              </>
            ) : (
              <>
                <h3>Используйте веб-версию</h3>
                <p style={{ marginBottom: 'var(--space-3)' }}>
                  Функционал организатора в Telegram отключён. Для доступа к личному кабинету зарегистрируйтесь или войдите по email в веб-версии.
                </p>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => {
                    localStorage.removeItem('token');
                    navigate('/login');
                  }}
                >
                  Перейти к регистрации
                </button>
              </>
            )}
          </div>
        )}

        <div className="profile-card">
          <div className="profile-row-head">
            <h2 className="profile-section-title">Мой тариф</h2>
            <span className={tierClass}>
              {profile.subscription_tier === 'corporate' ? 'Корпоративный' : profile.subscription_tier === 'advanced' ? 'Расширенный' : 'Бесплатный'}
            </span>
          </div>
          
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div className="profile-meter-row">
              <span>Создано опросов:</span>
              <span style={{ fontWeight: 700 }}>
                {pollMax === null ? `${profile.poll_count} · без ограничений` : `${profile.poll_count} / ${pollMax}`}
              </span>
            </div>
            {pollMax !== null && (
              <div className="profile-meter-track">
                <div
                  className="profile-meter-fill"
                  style={{
                    backgroundColor: meterColor,
                    width: `${Math.min(100, (profile.poll_count / pollMax) * 100)}%`
                  }}
                />
              </div>
            )}
          </div>

          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div className="profile-meter-row">
              <span>Активных опросов:</span>
              <span style={{ fontWeight: 700 }}>{profile.active_poll_count} / {activeMax}</span>
            </div>
            <div className="profile-meter-track">
              <div
                className="profile-meter-fill"
                style={{
                  backgroundColor: '#ea580c',
                  width: `${Math.min(100, (profile.active_poll_count / activeMax) * 100)}%`
                }}
              />
            </div>
          </div>

          {profile.subscription_expires_at && (
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginTop: 'var(--space-3)' }}>
              Срок действия: до {new Date(profile.subscription_expires_at).toLocaleDateString()}
            </div>
          )}

          {profile.company_name && (
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginTop: 'var(--space-2)' }}>
              Тариф предоставлен компанией «{profile.company_name}».
              {profile.personal_tier && profile.personal_tier !== 'free' && (
                <> После выхода из компании вернётся ваш тариф «{profile.personal_tier === 'advanced' ? 'Расширенный' : 'Корпоративный'}»
                  {profile.personal_expires_at ? ` (до ${new Date(profile.personal_expires_at).toLocaleDateString()})` : ''}.</>
              )}
            </div>
          )}

          {profile.subscription_tier === 'corporate' && (
            <div className="profile-logo-section">
              <h3>Логотип компании (Корпоративный)</h3>
              {logoError && <div className="profile-feedback profile-feedback--error">{logoError}</div>}
              {logoMessage && <div className="profile-feedback profile-feedback--success">{logoMessage}</div>}
              
              <div className="profile-logo-row">
                {profile.company_logo_path ? (
                  <>
                    <img 
                      src={`${api.defaults.baseURL.replace('/api', '')}/${profile.company_logo_path}`} 
                      alt="Company Logo" 
                      className="profile-logo-preview"
                    />
                    <div>
                        <button 
                            type="button"
                            className="btn btn--danger-outline btn--sm"
                            onClick={handleLogoDelete} 
                            disabled={isUploadingLogo}
                        >
                            Удалить логотип
                        </button>
                    </div>
                  </>
                ) : (
                  <div>
                    <input 
                        type="file" 
                        accept="image/*" 
                        id="logo-upload"
                        style={{ display: 'none' }}
                        onChange={handleLogoUpload}
                        disabled={isUploadingLogo}
                    />
                    <label 
                        htmlFor="logo-upload" 
                        className="btn btn--primary"
                        style={{ cursor: isUploadingLogo ? 'wait' : 'pointer', opacity: isUploadingLogo ? 0.7 : 1 }}
                    >
                        {isUploadingLogo ? 'Загрузка...' : 'Загрузить логотип'}
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="profile-card" id="tariffs">
          <h2 className="profile-section-title">Тарифы и оплата</h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <AdvancedPurchase profile={profile} onActivated={fetchProfile} />

            <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
              <h3>Корпоративный тариф</h3>
              <p style={{ margin: '0.5rem 0', color: 'var(--color-text-muted)' }}>Неограниченное количество опросов, 25 активных. Брендирование (логотип) и экспорт результатов в Excel.</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem' }}>
                <strong style={{ color: 'var(--color-primary)' }}>Пишите: info@svodx.ru</strong>
                <a href="mailto:info@svodx.ru" className="btn btn--secondary">Связаться с менеджером</a>
              </div>
            </div>
          </div>
        </div>

        <ReferralCard />

        <div className="profile-card">
          <h2 className="profile-section-title">Информация о пользователе</h2>
          <div className="profile-info-row">
            <span className="profile-info-label">Email:</span>
            <span className="profile-info-value">{profile.email || 'Не указан'}</span>
          </div>
        </div>

        {profile.email && <NewsletterCard profile={profile} onChanged={fetchProfile} />}

        {profile.email && (
          <div className="profile-card">
            <h2 className="profile-section-title">Изменить пароль</h2>
            {pwdMessage && <div className="profile-feedback profile-feedback--success">{pwdMessage}</div>}
            {pwdError && <div className="profile-feedback profile-feedback--error">{pwdError}</div>}
            
            <form onSubmit={handlePasswordChange}>
              <div className="profile-form-group">
                <label className="form-label" htmlFor="pwd-old">Старый пароль</label>
                <input 
                  id="pwd-old"
                  className="form-input"
                  type="password" 
                  value={oldPassword} 
                  onChange={e => setOldPassword(e.target.value)} 
                  required 
                />
              </div>
              <div className="profile-form-group">
                <label className="form-label" htmlFor="pwd-new">Новый пароль</label>
                <input 
                  id="pwd-new"
                  className="form-input"
                  type="password" 
                  value={newPassword} 
                  onChange={e => setNewPassword(e.target.value)} 
                  required 
                />
              </div>
              <div className="profile-form-group">
                <label className="form-label" htmlFor="pwd-confirm">Подтвердите новый пароль</label>
                <input 
                  id="pwd-confirm"
                  className="form-input"
                  type="password" 
                  value={confirmPassword} 
                  onChange={e => setConfirmPassword(e.target.value)} 
                  required 
                />
              </div>
              <button type="submit" disabled={isChangingPwd} className="btn btn--primary">
                {isChangingPwd ? 'Изменение...' : 'Сохранить пароль'}
              </button>
            </form>
          </div>
        )}

        {telegramWebAuthEnabled && canUnlink && (
          <div className="profile-card">
            <h2 className="profile-section-title">Управление подключениями</h2>
            <p className="profile-muted">
              Ваш аккаунт привязан как к Email, так и к Telegram. Вы можете отвязать Telegram аккаунт.
            </p>
            {unlinkMessage && <div className="profile-feedback profile-feedback--success">{unlinkMessage}</div>}
            {unlinkError && <div className="profile-feedback profile-feedback--error">{unlinkError}</div>}
            <button 
              type="button" 
              className="btn btn--danger-outline"
              onClick={handleUnlinkTelegram} 
              disabled={isUnlinking} 
            >
              {isUnlinking ? 'Отвязка...' : 'Отвязать Telegram'}
            </button>
          </div>
        )}

      </main>
    </div>
    </AppShell>
  );
}

export default Profile;
